/**
 * Order-by lines of the delivery promise: writes the order-by day in the shopper's language and, in the last
 * hours (data-countdown-hours), the time left until the end of that day in shop time — days and hours, then
 * hours and minutes, then seconds in the last hour. The deadline is real (counted back from the occasion by the
 * market's longest delivery time), and a line hides itself once its moment has passed, because a cached page can
 * outlive it.
 */
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;

function formatDay(/** @type {string} */ value, /** @type {string} */ locale) {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
      new Date(Date.UTC(year, month - 1, day, 12))
    );
  } catch {
    return null;
  }
}

function fill(/** @type {string} */ template, /** @type {Record<string, string | number>} */ values) {
  return template.replace(/%(\w+)%/g, (match, key) => String(values[key] ?? match));
}

const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');

for (const line of document.querySelectorAll('[data-deadline]')) {
  if (!(line instanceof HTMLElement) || line.dataset.ready) continue;
  line.dataset.ready = 'true';
  const end = Number(line.dataset.deadline) * SECOND;
  if (!end) continue;

  const day = line.querySelector('[data-deadline-date]');
  if (day instanceof HTMLTimeElement) {
    const text = formatDay(day.dateTime, document.documentElement.lang || 'en');
    if (text) day.textContent = text;
  }

  const left = line.querySelector('[data-deadline-left]');
  const countdownFrom = (Number(line.dataset.countdownHours) || 0) * HOUR;
  const { templateLeft = '', templateDays = '', templateHours = '', templateMinutes = '' } = line.dataset;

  const tick = () => {
    const remaining = end - Date.now();
    if (remaining <= 0) {
      line.hidden = true;
      return;
    }

    // Outside the countdown a check a minute is enough; inside it, wake when the shown value changes.
    let delay = MINUTE;
    if (left instanceof HTMLElement && remaining <= countdownFrom) {
      const seconds = Math.floor(remaining / SECOND);
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      let time;
      if (days > 0) {
        time = fill(templateDays, { D: days, H: hours });
      } else if (hours > 0) {
        time = fill(templateHours, { H: hours, M: pad(minutes) });
      } else {
        time = fill(templateMinutes, { M: minutes, S: pad(seconds % 60) });
      }
      left.textContent = fill(templateLeft, { TIME: time });
      left.hidden = false;
      delay = (remaining < HOUR ? remaining % SECOND : remaining % MINUTE) + 20;
    }
    setTimeout(tick, Math.min(delay, remaining));
  };
  tick();
}
